import { AUTH_ROUTES } from "./authRoutes";
import React, { useState } from 'react';
import { Alert, Button, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import "./SignIn.css";
import ForgotBg from "../assets/images/forgot-bg.svg";
import ForgotMobile from "../assets/images/forgot-mobile.svg";
import { BackSVG } from '../components/SvgIcons';
import { useMediaQuery } from "react-responsive";
import VerifyModal from '../components/VerifyModal';
import { resetForgottenPassword } from '../services/authService';

function NewPwd() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const userId = location.state?.userId;
  const email = location.state?.email || '';

  const [show, setShow] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!userId) {
    navigate(AUTH_ROUTES.forgotPassword, { replace: true });
    return null;
  }

  const validateForm = () => {
    const nextErrors = {};

    if (!passwords.password) {
      nextErrors.password = 'New password is required.';
    } else if (passwords.password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@!$#%]).{6,}$/.test(passwords.password)) {
      nextErrors.password = 'Password must include a letter, a number, and a special character (@!$#%).';
    }

    if (!passwords.confirmPassword) {
      nextErrors.confirmPassword = 'Confirm password is required.';
    } else if (passwords.confirmPassword !== passwords.password) {
      nextErrors.confirmPassword = 'Confirm password must match the new password.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setPasswords((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));
    setSubmitError('');
  };

  const handleVerify = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await resetForgottenPassword({
        user_id: userId,
        password: passwords.password,
        confirm_password: passwords.confirmPassword,
      });
      setShow(true);
    } catch (error) {
      setSubmitError(error.message || 'Unable to reset password right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid signin-container auth-background">
      <div className="row auth-page-row">
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-section p-0 verifiy-LeftSection">
          <div className="verificationBg">
            <img src={ForgotBg} alt="" className="img-fluid mb-4" />
          </div>
        </div>

        <div className="col-md-6 d-flex justify-content-center align-items-center right-section verification-box mobile-display">
          <div className="backIcon ps-4 pe-4 d-block d-md-none" onClick={() => navigate(AUTH_ROUTES.checkEmail, { state: { userId, email } })}>
            <BackSVG />
          </div>
          <div className="signin-mobile ps-4 pe-4 verify-mobile d-block d-md-none mx-auto text-center">
            <img src={ForgotMobile} alt="" className="mx-auto" />
          </div>

          <div className="signin-box auth-form-card p-4 p-md-5 rounded">
            <h3 className="fw-semibold text-center mb-2">Create new Password</h3>
            <p className="text-center fw-medium mobile-paragraph auth-form-intro">
              This password should be different from the previous password.
            </p>

            {submitError ? (
              <Alert className="mb-3" variant="danger">
                {submitError}
              </Alert>
            ) : null}

            <Form onSubmit={handleVerify}>
              <Form.Group className="mb-20">
                <Form.Label>New Password</Form.Label>
                <div className="auth-form-field auth-password-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-lock" /></span>
                  <Form.Control
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder={isMobile ? "New Password" : "Enter Password"}
                    name="password"
                    value={passwords.password}
                    isInvalid={Boolean(fieldErrors.password)}
                    onChange={handleChange}
                  />
                  <button
                    className="auth-password-toggle"
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    <i className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" />
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.password}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-20">
                <Form.Label>Confirm New Password</Form.Label>
                <div className="auth-form-field auth-password-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-lock" /></span>
                  <Form.Control
                    autoComplete="new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={isMobile ? "Confirm Password" : "Enter Password"}
                    name="confirmPassword"
                    value={passwords.confirmPassword}
                    isInvalid={Boolean(fieldErrors.confirmPassword)}
                    onChange={handleChange}
                  />
                  <button
                    className="auth-password-toggle"
                    type="button"
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                    aria-pressed={showConfirmPassword}
                    onClick={() => setShowConfirmPassword((current) => !current)}
                  >
                    <i className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" />
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.confirmPassword}</Form.Control.Feedback>
              </Form.Group>

              <Button className="w-100 py-2 fw-semibold signin-btn mt-4" disabled={isSubmitting} type="submit">
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Form>

            <VerifyModal
              show={show}
              onHide={() => {
                setShow(false);
                navigate(AUTH_ROUTES.signIn, { replace: true });
              }}
              title="Successfully"
              message="Congratulations, your password has been successfully created."
              buttonText="Proceed"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewPwd;
